import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, Button, Alert, Spinner, Row, Col, Badge, Table } from 'react-bootstrap'
import { useAdminAnalyticData } from 'api/hooks/use-admin-analytics-data'
import AdminTabs from 'admin/shared/AdminTabs'
import AppBadge from 'shared/badges/app-badge'
import AnalyticMunicipalitiesTab, { rowError } from './AnalyticMunicipalitiesTab'
import AnalyticConfigTab from './AnalyticConfigTab'

const SummaryStat = ({ label, value, variant = 'primary', onClick }) => (
  <Col sm={6} lg={3}>
    <Card className="h-100 shadow-sm" role="button" onClick={onClick}>
      <Card.Body>
        <div className={`h4 text-${variant} mb-0`}>{value}</div>
        <span className="text-muted">{label}</span>
      </Card.Body>
    </Card>
  </Col>
)

const AnalyticDataDetail = () => {
  const { id } = useParams()
  const { data: analytic, isLoading, error } = useAdminAnalyticData({ id })
  const [activeTab, setActiveTab] = useState('details')

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  if (error) {
    return <Alert variant="danger">Error loading analytic: {error.message}</Alert>
  }

  if (!analytic) {
    return <Alert variant="warning">Analytic not found.</Alert>
  }

  const municipalities = analytic.municipalities || []
  const totalFlags = municipalities.reduce((sum, row) => sum + (row.analytics_count || 0), 0)
  const flagging = municipalities.filter((row) => row.analytics_count > 0).length
  const errors = municipalities.filter(rowError).length
  const showMunicipalities = () => setActiveTab('municipalities')
  // Custom analytics are recipes; the generic form can't edit a recipe.
  const editPath = analytic.custom
    ? `/admin/municipalities/${analytic.municipality_id}/custom-analytics`
    : `/admin/analytics-data/${id}/edit`

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h2 mb-1">{analytic.key} - {analytic.name}</h1>
          {analytic.custom ? (
            <AppBadge variant="ai">Custom · {analytic.municipality_name}</AppBadge>
          ) : (
            <AppBadge variant="primary">{analytic.is_core ? 'Default' : 'Default · Off'}</AppBadge>
          )}
        </div>
        <div>
          <Button as={Link} to={editPath} variant="warning" className="me-2">
            {analytic.custom ? 'Edit in Recipe Builder' : 'Edit Analytic'}
          </Button>
          <Button as={Link} to="/admin/analytics-data" variant="light-primary">Back to List</Button>
        </div>
      </div>

      <Row className="g-3 mb-4">
        <SummaryStat label="Municipalities with the module" value={municipalities.length} onClick={showMunicipalities} />
        <SummaryStat label="Open flags" value={totalFlags.toLocaleString()} onClick={showMunicipalities} />
        <SummaryStat label="Municipalities flagging" value={flagging} onClick={showMunicipalities} />
        <SummaryStat
          label="Municipalities with errors"
          value={errors}
          variant={errors > 0 ? 'danger' : 'primary'}
          onClick={showMunicipalities}
        />
      </Row>

      <AdminTabs
        tabs={[
          { name: 'Details', value: 'details' },
          { name: `Municipalities (${municipalities.length})`, value: 'municipalities' },
          { name: `Configuration (${analytic.config_keys?.length || 0})`, value: 'configuration' },
          { name: `Table Columns (${analytic.analytic_table_columns?.length || 0})`, value: 'table-columns' }
        ]}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {activeTab === 'details' && (
        <Row>
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header><h5>Basic Information</h5></Card.Header>
              <Card.Body>
                <dl className="row mb-0">
                  <dt className="col-sm-5">Key:</dt>
                  <dd className="col-sm-7"><code>{analytic.key}</code></dd>
                  <dt className="col-sm-5">Name:</dt>
                  <dd className="col-sm-7">{analytic.name}</dd>
                  <dt className="col-sm-5">Code:</dt>
                  <dd className="col-sm-7">{analytic.code || 'N/A'}</dd>
                  <dt className="col-sm-5">Module:</dt>
                  <dd className="col-sm-7">
                    {analytic.section_abbreviation ? (
                      <Link to={`/admin/sections/${analytic.section_id}`}>
                        <Badge bg="info">{analytic.section_name} ({analytic.section_abbreviation})</Badge>
                      </Link>
                    ) : 'N/A'}
                  </dd>
                  <dt className="col-sm-5">Status:</dt>
                  <dd className="col-sm-7">
                    <span className="text-muted">{analytic.is_core ? 'On' : 'Off'}</span>
                  </dd>
                  <dt className="col-sm-5">Risk Score:</dt>
                  <dd className="col-sm-7">{analytic.risk_score}</dd>
                  <dt className="col-sm-5">Tags:</dt>
                  <dd className="col-sm-7">
                    {analytic.tags?.length > 0
                      ? analytic.tags.map((tag) => (
                          <Badge key={tag.id} pill className="border border-light text-primary bg-white me-1">{tag.name}</Badge>
                        ))
                      : 'None'}
                  </dd>
                </dl>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header><h5>Descriptions</h5></Card.Header>
              <Card.Body>
                <h6>Description</h6>
                <p>{analytic.description || 'N/A'}</p>
                <h6>Risk</h6>
                <p>{analytic.risk || 'N/A'}</p>
                <h6>Description Template</h6>
                <p>{analytic.description_template || 'N/A'}</p>
                <h6>Filters</h6>
                <p>{analytic.filters || 'N/A'}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === 'municipalities' && <AnalyticMunicipalitiesTab analytic={analytic} />}

      {activeTab === 'configuration' && <AnalyticConfigTab analytic={analytic} />}

      {activeTab === 'table-columns' && (
        <Card>
          <Card.Body>
            {analytic.analytic_table_columns?.length > 0 ? (
              <Table bordered hover responsive>
                <thead>
                  <tr>
                    <th>Placeholder Key</th>
                    <th>Table Column</th>
                    <th>Position</th>
                  </tr>
                </thead>
                <tbody>
                  {analytic.analytic_table_columns.map((atc) => (
                    <tr key={atc.id}>
                      <td><code>{`{{${atc.placeholder_key}}}`}</code></td>
                      <td>{atc.table_column_name}</td>
                      <td><span className="text-muted">{atc.position}</span></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info" className="mb-0">No table columns linked to this analytic.</Alert>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

export default AnalyticDataDetail
